export type ProductBrandId = "podscope" | "conscope";

export type ProductBrand = {
  id: ProductBrandId;
  name: string;
  markLetter: string;
  nameRemainder: string;
  tagline: string;
  marketingUrl: string;
  appUrl: string;
  supportEmail: string;
  description: string;
  themeColor: string;
  twitterHandle: string;
  guidesLabel: string;
  faviconHref: string;
  wordmarkDarkSrc: string;
  wordmarkLightSrc: string;
  usesImageWordmark: boolean;
  lightPrimary: {
    main: string;
    light: string;
    dark: string;
  };
  fontFamily: string;
  headingFontFamily: string;
  wordmarkFontFamily: string;
  bodyFontWeight: number;
  headingFontWeight: number;
  textPrimary: string;
};

const PODSCOPE_FONT =
  '"Inter", "Roboto", "DM Sans", "Arial", sans-serif';
const PODSCOPE_HEADING_FONT = '"DM Serif Display", serif';
const PODSCOPE_WORDMARK_FONT = '"DM Serif Text", serif';
const CONSCOPE_FONT =
  '"Helvetica Neue", Helvetica, Arial, sans-serif';
const CONSCOPE_HEADING_FONT =
  '"Space Grotesk", "Helvetica Neue", Helvetica, Arial, sans-serif';

const PUBLIC = process.env.PUBLIC_URL ?? "";

const PODSCOPE: ProductBrand = {
  id: "podscope",
  name: "Podscope",
  markLetter: "P",
  nameRemainder: "odscope",
  tagline: "Análisis de Podcasts",
  marketingUrl: "https://podscope.co",
  appUrl: "https://app.podscope.co",
  supportEmail: "esteban@podscope.co",
  description:
    "Descubre dónde y cómo están invirtiendo las marcas en podcasts de LATAM. Analiza tendencias de inversión, alcance y efectividad de auspiciadores en la región.",
  themeColor: "#1A1930",
  twitterHandle: "@podscope",
  guidesLabel: "Guías",
  faviconHref: `${PUBLIC}/favicon.ico`,
  wordmarkDarkSrc: `${PUBLIC}/podscope-wordmark-dark.png`,
  wordmarkLightSrc: `${PUBLIC}/podscope-wordmark-light.png`,
  usesImageWordmark: true,
  lightPrimary: {
    main: "#1A1930",
    light: "rgba(26, 25, 48, 0.2)",
    dark: "#0F0E1D",
  },
  fontFamily: PODSCOPE_FONT,
  headingFontFamily: PODSCOPE_HEADING_FONT,
  wordmarkFontFamily: PODSCOPE_WORDMARK_FONT,
  bodyFontWeight: 400,
  headingFontWeight: 700,
  textPrimary: "#0E0E0E",
};

const CONSCOPE: ProductBrand = {
  id: "conscope",
  name: "Conscope",
  markLetter: "C",
  nameRemainder: "onscope",
  tagline: "Medios digitales",
  marketingUrl: "https://conscope.co",
  appUrl: "https://app.conscope.co",
  supportEmail: "esteban@podscope.co",
  description:
    "Conscope – Medimos y verificamos programas, streaming en vivo y clips en medios digitales.",
  themeColor: "#652DD0",
  twitterHandle: "@conscope",
  guidesLabel: "Guías",
  faviconHref: `${PUBLIC}/conscope-favicon.png`,
  wordmarkDarkSrc: `${PUBLIC}/conscope-logo.png`,
  wordmarkLightSrc: `${PUBLIC}/conscope-logo.png`,
  usesImageWordmark: false,
  lightPrimary: {
    main: "#131116",
    light: "rgba(19, 17, 22, 0.2)",
    dark: "#0A090B",
  },
  fontFamily: CONSCOPE_FONT,
  headingFontFamily: CONSCOPE_HEADING_FONT,
  wordmarkFontFamily: CONSCOPE_HEADING_FONT,
  bodyFontWeight: 500,
  headingFontWeight: 500,
  textPrimary: "#0E0E0E",
};

const BRANDS: Record<ProductBrandId, ProductBrand> = {
  podscope: PODSCOPE,
  conscope: CONSCOPE,
};

export function resolveProductBrandId(
  raw: string | undefined = process.env.REACT_APP_PRODUCT_BRAND,
): ProductBrandId {
  return raw?.trim().toLowerCase() === "conscope" ? "conscope" : "podscope";
}

export function resolveProductBrand(
  raw: string | undefined = process.env.REACT_APP_PRODUCT_BRAND,
): ProductBrand {
  const brand = BRANDS[resolveProductBrandId(raw)];
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL?.trim();
  if (supportEmail) {
    return { ...brand, supportEmail };
  }
  return brand;
}

export const productBrand = resolveProductBrand();

export function formatProductTitle(title?: string): string {
  const name = productBrand.name;
  if (!title || title === name) return name;
  return title.includes(name) ? title : `${title} | ${name}`;
}
